import {
	authInterceptorFactory,
	axios,
	requestHeaderNormalizerInterceptor,
	piggyBack,
	extendGoal,
	extendKeyResult
} from "@teaming/io-helpers";
import { goalCache, goalActivityCache } from "../iocache";

const IO_NAMESPACE = "gokr";
const API_VERSION_THREE = "3";

export default function ioGokrFactory( { config: { baseUri, }, authService, } ) {
	axios.defaults.baseURL = baseUri;
	axios.interceptors.request.use( requestHeaderNormalizerInterceptor );
	axios.interceptors.request.use( authInterceptorFactory( { authService, } ) );

	const fetchGoal = piggyBack( async ( { goalId, }, { refetch, } = { refetch: false, } ) => {
		let result = goalCache.get( goalId );
		if ( result && !refetch ) {
			return result;
		}
		const url = `/goal/${ goalId }`;
		const method = "GET";
		result = await axios( {
			url,
			method,
			headers: {
				"x-api-version": API_VERSION_THREE,
			},
		} );
		result.data = extendGoal( result.data );
		goalCache.set( goalId, result );
		return result;
	} );

	const fetchGoals = piggyBack( async ( { offset, limit, teamId, type, } ) => {
		const params = { offset, limit, teamId, type, };
		const url = "/goal";
		const method = "GET";
		const result = await axios( {
			url,
			method,
			params,
			headers: {
				"x-api-version": API_VERSION_THREE,
			},
		} );

		for ( const goal of result.data.items ) {
			extendGoal( goal );
			goalCache.set( goal.id, {
				...result,
				data: goal,
			} );
		}

		return result;
	} );

	const fetchGoalMetadata = piggyBack( async () => {
		const url = "/goal/meta";
		const method = "GET";
		return axios( { url, method, } );
	} );

	async function saveGoal( { goal, } ) {
		let method = "POST";
		let url = "/goal";

		if ( goal.id ) {
			method = "PUT";
			url += `/${ goal.id }`;
		}

		const savedGoal = await axios( {
			method,
			url,
			headers: {
				"x-api-version": API_VERSION_THREE,
			},
			data: goal,
		} );

		goalCache.remove( savedGoal.id );

		return savedGoal;
	}

	async function upfetchGoal( { goal, } ) {
		const { data: savedGoal, } = await saveGoal( { goal, } );
		return await fetchGoal( { goalId: goal.id || savedGoal.id, }, { refetch: true, } );
	}

	async function deleteGoal( { goalId, } ) {
		const method = "DELETE";
		const url = `/goal/${ goalId }`;

		await axios( {
			method,
			url,
		} );

		goalCache.remove( goalId );
	}

	async function archiveGoal( { goal, } ) {
		goal = {
			...goal,
			archived: true,
		};
		await saveGoal( { goal, } );
		return await fetchGoal( { goalId: goal.id, }, { refetch: true, } );
	}

	async function unarchiveGoal( { goal, } ) {
		goal = {
			...goal,
			archived: false,
		};
		await saveGoal( { goal, } );
		return await fetchGoal( { goalId: goal.id, }, { refetch: true, } );
	}

	async function saveKeyResult( { keyResult, } ) {
		let method = "POST";
		let url = `/goal/${ keyResult.goalId }/keyresult`;

		if ( keyResult.id ) {
			method = "PUT";
			url += `/${ keyResult.id }`;
		}

		const response = await axios( {
			method,
			url,
			data: keyResult,
		} );

		goalCache.remove( keyResult.goalId );

		const { data: updatedKeyResult, } = response;
		extendKeyResult( updatedKeyResult );

		return response;
	}

	async function upfetchKeyResult( { keyResult, } ) {
		const { data: updatedKeyResult, } = await saveKeyResult( { keyResult, } );
		const { goalId, } = updatedKeyResult;
		const { data: goal, } = await fetchGoal( { goalId, }, { refetch: true, } );
		// FIXME: the KR object returned from saveKeyResult() lacks progress and pacing properties
		// (and maybe others?) that are present on the KR as it appears in a loaded goal. We
		// are re-loading the goal, with it's KRs here, in order to compute these properties until this can
		// be normalized in services.
		//
		// This is sad and makes me cry. Have added a card to address this.
		extendGoal( goal );
		return goal.keyResults.find( kr => kr.id === updatedKeyResult.id ) || updatedKeyResult;
	}

	async function deleteKeyResult( { keyResultId, goalId, } ) {
		const method = "DELETE";
		const url = `/goal/${ goalId }/keyresult/${ keyResultId }`;

		await axios( {
			method,
			url,
		} );

		goalCache.remove( goalId );
	}

	const fetchGoalActivity = piggyBack( async ( { goalId, }, { refetch = false, } = { refetch: false, } ) => {
		if ( !refetch && goalActivityCache.has( goalId ) ) {
			return goalActivityCache.get( goalId );
		}
		const url = `/goal/${ goalId }/activity`;
		const method = "GET";
		const response = await axios( { url, method, } );
		goalActivityCache.set( goalId, response );
		return response;
	}, { timeout: 500, } );

	async function createGoalComment( { goalId, comment, } ) {
		if ( !goalId ) { throw new Error( "missing param: goalId" ); }
		if ( !comment ) { throw new Error( "missing param: comment" ); }

		const method = "POST";
		const url = `/goal/${ goalId }/comment`;

		const savedGoal = await axios( {
			method,
			url,
			data: { comment, },
		} );

		goalActivityCache.remove( goalId );

		return savedGoal;
	}

	async function createKeyResultComment( { goalId, keyResultId, comment, } ) {
		if ( !goalId ) { throw new Error( "missing param: goalId" ); }
		if ( !keyResultId ) { throw new Error( "missing param: keyResultId" ); }
		if ( !comment ) { throw new Error( "missing param: comment" ); }

		const method = "POST";
		const url = `/goal/${ goalId }/keyresult/${ keyResultId }/comment`;

		const savedGoal = await axios( {
			method,
			url,
			data: { comment, },
		} );

		goalActivityCache.remove( goalId );

		return savedGoal;
	}

	return {
		namespace: IO_NAMESPACE,
		saveGoal,
		deleteGoal,
		archiveGoal,
		unarchiveGoal,
		saveKeyResult,
		deleteKeyResult,
		fetchGoal,
		fetchGoals,
		fetchGoalMetadata,
		upfetchGoal,
		upfetchKeyResult,
		fetchGoalActivity,
		createGoalComment,
		createKeyResultComment,
	};
}
